/**
 * Embeddable care-time entry fields.
 *
 * Controlled component — the parent owns the state and decides when to
 * call the care-time facade. Used inside:
 *  - Quick-add modal (patient profile, action list)
 *  - Intake completion content (call-time + documentation-time sections)
 *  - Default progress content (optional minutes capture)
 *  - Activity-log drawer (inline edit row)
 *
 * Keeps validation light: minutes ≥ 1 enforced visually; the action
 * layer rejects invalid input authoritatively.
 *
 * @agentic connections
 */

import { NumberInput, Stack, Switch, Text, Textarea } from "@mantine/core";
import { IconClockHour4 } from "@tabler/icons-react";

/** Props for `CareTimeInlineFields`. @agentic connections */
export type CareTimeInlineFieldsProps = {
  /** Current minutes value (undefined = not yet entered). */
  minutes: number | undefined;
  /** Notify the parent of minute changes. */
  onMinutesChange: (value: number | undefined) => void;
  /** Current notes value. */
  notes: string;
  /** Notify the parent of note changes. */
  onNotesChange: (value: string) => void;
  /** Section label; defaults to "Care time". */
  label?: string;
  /** Helper text shown below the minutes input. */
  helperText?: string;
  /** Marks the minutes input required (visual cue only). */
  required?: boolean;
  /** Disables both inputs (e.g. while submitting). */
  disabled?: boolean;
  /**
   * Hard upper bound for the minutes input. Defaults to 480 (8h)
   * matching the plan's "large value warning" threshold.
   */
  maxMinutes?: number;
  /** Show notes field. Defaults to true. */
  showNotes?: boolean;
  /**
   * Show the "Exclude from CoCM billing (non-billable)" toggle. Opt-in so
   * only review surfaces (activity log, quick-add) render it. Defaults to false.
   */
  showNonBillableToggle?: boolean;
  /** Current non-billable value (true = excluded from billing). */
  nonBillable?: boolean;
  /** Notify the parent of non-billable changes. */
  onNonBillableChange?: (value: boolean) => void;
};

const DEFAULT_MAX_MINUTES = 480;

/**
 * Embeddable controlled care-time entry fields (minutes + notes).
 * @agentic connections
 */
export const CareTimeInlineFields = ({
  minutes,
  onMinutesChange,
  notes,
  onNotesChange,
  label = "Care time",
  helperText,
  required = false,
  disabled = false,
  maxMinutes = DEFAULT_MAX_MINUTES,
  showNotes = true,
  showNonBillableToggle = false,
  nonBillable = false,
  onNonBillableChange,
}: CareTimeInlineFieldsProps) => (
  <Stack gap="xs">
    <Text size="sm" fw={500}>
      {label}
      {required ? " *" : ""}
    </Text>
    <NumberInput
      value={minutes ?? ""}
      onChange={(value) => {
        if (value === "" || value === null || value === undefined) {
          onMinutesChange(undefined);
          return;
        }
        const num = typeof value === "number" ? value : Number(value);
        onMinutesChange(Number.isFinite(num) ? num : undefined);
      }}
      placeholder="0"
      min={0}
      max={maxMinutes}
      step={1}
      decimalScale={0}
      hideControls={false}
      leftSection={<IconClockHour4 size={16} />}
      rightSection={<Text size="sm">min</Text>}
      rightSectionWidth={40}
      description={helperText}
      disabled={disabled}
      aria-label={`${label} minutes`}
    />
    {showNotes ? (
      <Textarea
        value={notes}
        onChange={(event) => onNotesChange(event.currentTarget.value)}
        placeholder="Notes (optional)"
        minRows={2}
        autosize
        disabled={disabled}
        aria-label={`${label} notes`}
      />
    ) : null}
    {showNonBillableToggle ? (
      <Switch
        checked={nonBillable}
        onChange={(event) => onNonBillableChange?.(event.currentTarget.checked)}
        label="Exclude from CoCM billing (non-billable)"
        description="Excluded minutes stay in the activity log but do not count toward CoCM billing."
        disabled={disabled}
        aria-label="Exclude from CoCM billing (non-billable)"
      />
    ) : null}
  </Stack>
);
